import { useNavigate } from "react-router";
import { useQueryClient } from "@tanstack/react-query";

import { useAdminOrgStore } from "@/stores/admin-org";

/**
 * The route from an all-org admin screen to one org-scoped event (POS2-1120).
 *
 * `/waste/events/:id` is org-scoped, and the admin lists that link here span every org — so
 * the org context is set and the query cache dropped before navigating, exactly as
 * `admin/monitoring.tsx`'s drill-down does. Without both, the event page fetches under
 * whichever `X-Org-Id` the admin was last looking at and 404s.
 *
 * The short id stays the visible handle: it is what the admin reads out and matches against
 * the workstation, and eight characters is enough to tell two rows apart. Styled as the
 * design system's link — blue-600, underline on hover only — at the mono size the exceptions
 * column already used, with the full id and the org on the tooltip.
 *
 * Lives here rather than in `pages/admin/labelling.tsx` because POS2-1123's flagged-captures
 * dialog is its second consumer, and the two things that make it correct — setting the org
 * *and* dropping the cache — are exactly what a second copy would get wrong.
 */
export function EventLink({
  eventId,
  orgId,
  orgName,
}: {
  eventId: string;
  orgId: string;
  orgName: string;
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const setOrg = useAdminOrgStore((s) => s.setOrg);
  return (
    <button
      type="button"
      title={`${eventId} · ${orgName}`}
      className="font-mono text-xs text-blue-600 hover:text-blue-700 hover:underline"
      onClick={() => {
        setOrg(orgId, orgName);
        queryClient.removeQueries();
        navigate(`/waste/events/${eventId}`);
      }}
    >
      {eventId.slice(0, 8)}
    </button>
  );
}
